import {
  ArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  DateTimeInput,
  FormDataConsumer,
  maxValue,
  minValue,
  NullableBooleanInput,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  regex,
  SelectArrayInput,
  SimpleFormIterator,
  TextInput,
} from 'react-admin';
import { useTranslate } from 'ra-core';
import _ from 'lodash';
import { ReactNode, useId, useState } from 'react';
import { Chip } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import {
  getOneOfVariants,
  isDiscriminatedObjectOneOf,
  pickOneOfVariant,
  resolveSchemaForForm,
  STRING_OR_ENUM_ARRAY_UNION,
} from './clientConfigSchemaResolve';

const indentBorderStyle = '2px solid #e0e0e0';

const translateResource = (resource: string) => {

  const splitResource = resource.split(/(?=[A-Z])/);
  const joinedResource = splitResource.join('-');

  return `${joinedResource.toLowerCase()}s`;

};

const isColor = (strColor: string) => {

  const s = new Option().style;
  s.color = strColor;
  return s.color !== '';

};

/** Extract enum choices from a JSON Schema node (enum, anyOf/oneOf const unions). */
export const getSchemaEnumChoices = (schema: any): unknown[] | undefined => {

  if (!schema) {

    return undefined;

  }
  if (schema.enum) {

    return schema.enum;

  }
  const union = schema.anyOf ?? schema.oneOf;
  if (Array.isArray(union)) {

    const fromConst = union
      .map((entry: any) => entry?.const ?? (entry?.enum?.length === 1 ? entry.enum[0] : undefined))
      .filter((value: unknown) => value !== undefined);
    if (fromConst.length === union.length) {

      return fromConst;

    }

  }
  return undefined;

};

export const getDepth = (key: string, allKeys: string[]) => {

  const currentSplitKey = key.split('.');
  let depth = 0;
  for (let i = currentSplitKey.length - 1; i > 0; i--) {

    const currentKey = currentSplitKey.slice(0, i).join('.');
    const hasSiblings = allKeys.some((innerKey) => innerKey.startsWith(currentKey) && innerKey !== key);
    if (hasSiblings) {

      depth = i;
      break;

    }

  }

  return depth;

};

export const getTitleFromKey = (key: string, index?: number) => {

  const splitKey = key.split('.');
  const title = splitKey[index !== undefined ? index : splitKey.length - 1]?.replace(/([A-Z])/g, ' $1');
  if (!title) {

    return '';

  }
  return title[0].toUpperCase() + title.slice(1).toLowerCase();

};

export type ClientConfigSchemaFieldDefinition = {
  key: string;
  schema?: any;
  editableBy?: ('Customer' | 'AssetGroup' | 'Device')[];
  mergeHistory?: any[];
};

const collectSchemaPropertyPaths = (schema: any, prefix = ''): string[] => {

  const resolved = resolveSchemaForForm(schema) ?? schema;
  if (!resolved?.properties) {

    return prefix ? [prefix] : [];

  }
  const paths: string[] = [];
  for (const key of Object.keys(resolved.properties)) {

    const path = prefix ? `${prefix}.${key}` : key;
    const propSchema = resolveSchemaForForm(resolved.properties[key]) ?? resolved.properties[key];
    if (propSchema?.type === 'object' && propSchema.properties && !propSchema.properties._ref) {

      paths.push(...collectSchemaPropertyPaths(propSchema, path));

    } else {

      paths.push(path);

    }

  }
  return paths;

};

const getCommonPathDepth = (left: string, right: string): number => {

  const leftParts = left.split('.');
  const rightParts = right.split('.');
  const limit = Math.min(leftParts.length, rightParts.length);
  let depth = 0;
  while (depth < limit && leftParts[depth] === rightParts[depth]) {

    depth += 1;

  }
  return depth;

};

const getSectionTitleBetweenPaths = (previousPath: string | undefined, currentPath: string): string => {

  if (!previousPath) {

    const depth = currentPath.split('.').length;
    if (depth > 1) {

      return getTitleFromKey(currentPath, depth - 2);

    }
    return '';

  }

  const previousParts = previousPath.split('.');
  const currentParts = currentPath.split('.');
  const shareParentBranch = previousParts.length > 0
    && currentParts.length > 0
    && previousParts.slice(0, -1).join('.') === currentParts.slice(0, -1).join('.');

  if (shareParentBranch) {

    return '';

  }

  const commonDepth = getCommonPathDepth(previousPath, currentPath);
  if (commonDepth < currentParts.length) {

    return getTitleFromKey(currentParts[commonDepth]);

  }
  return '';

};

export type SchemaSection<T> = {
  title: string | null;
  items: T[];
};

export function groupBySchemaSection<T>(
  items: T[],
  getPropertyKey: (item: T) => string,
  sourcePrefix: string,
): SchemaSection<T>[] {

  const sections: SchemaSection<T>[] = [];
  let current: SchemaSection<T> = { title: null, items: [] };

  items.forEach((item, index) => {

    const key = getPropertyKey(item);
    const source = sourcePrefix ? `${sourcePrefix}.${key}` : key;
    const previousKey = index > 0 ? getPropertyKey(items[index - 1]) : undefined;
    let previousPath: string | undefined;
    if (previousKey) {

      previousPath = sourcePrefix ? `${sourcePrefix}.${previousKey}` : previousKey;

    }
    const sectionTitle = getSectionTitleBetweenPaths(previousPath, source);
    if (sectionTitle) {

      if (current.items.length > 0) {

        sections.push(current);

      }
      current = { title: sectionTitle, items: [item] };

    } else {

      current.items.push(item);

    }

  });
  if (current.items.length > 0) {

    sections.push(current);

  }
  return sections;

}

export const CollapsibleConfigSection = (props: {
  title: string;
  headingLevel?: 'h1' | 'h2';
  defaultCollapsed?: boolean;
  hitCount?: number;
  lazyMount?: boolean;
  children: ReactNode;
}) => {

  const {
    title,
    headingLevel = 'h2',
    defaultCollapsed = true,
    hitCount = 0,
    lazyMount = true,
    children,
  } = props;
  const contentId = useId();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [mounted, setMounted] = useState(!lazyMount || !defaultCollapsed);

  const toggle = () => {

    if (collapsed && lazyMount) {

      setMounted(true);

    }
    setCollapsed((value) => !value);

  };
  const HeadingTag = headingLevel;

  return (
    <div style={{ marginTop: '1em' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          marginBottom: '0.5em',
        }}
        onClick={toggle}
        tabIndex={0}
        onKeyDown={(event) => {

          if (event.key === 'Enter' || event.key === ' ') {

            event.preventDefault();
            toggle();

          }

        }}
        aria-expanded={!collapsed}
        aria-controls={contentId}
        role="button"
      >
        <PlayArrow
          sx={{
            transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
            transition: 'transform 0.2s',
            marginRight: '0.5em',
          }}
        />
        <HeadingTag style={{ margin: 0, fontSize: headingLevel === 'h1' ? '20px' : '18px' }}>
          {title}
        </HeadingTag>
        {hitCount > 0 && (
          <Chip
            label={hitCount}
            size="small"
            sx={{ marginLeft: '0.75em' }}
          />
        )}
      </div>
      {(mounted || !lazyMount) && (
        <div
          id={contentId}
          style={{ display: collapsed ? 'none' : 'block' }}
        >
          {children}
        </div>
      )}
    </div>
  );

};

function SchemaObjectPropertyFields(props: {
  schema: any;
  sourcePrefix: string;
  editableBy?: ClientConfigSchemaFieldDefinition['editableBy'];
  mergeHistory?: ClientConfigSchemaFieldDefinition['mergeHistory'];
  fallback?: ReactNode;
  lazyMountSections?: boolean;
}) {

  const {
    schema, sourcePrefix, editableBy, mergeHistory, fallback, lazyMountSections = true,
  } = props;
  const resolvedSchema = resolveSchemaForForm(schema) ?? schema;
  if (!resolvedSchema?.properties) {

    return null;

  }

  const propertyKeys = Object.keys(resolvedSchema.properties);
  const allPaths = collectSchemaPropertyPaths(resolvedSchema, sourcePrefix);
  const sections = groupBySchemaSection(propertyKeys, (key) => key, sourcePrefix);

  const renderPropertyField = (key: string) => {

    const source = sourcePrefix ? `${sourcePrefix}.${key}` : key;
    const isPropertyRequired = resolvedSchema.required?.includes(key);
    const calcDepth = getDepth(source, allPaths);
    return (
      <div
        style={{
          paddingLeft: `${calcDepth}em`,
          borderLeft: calcDepth > 0 ? indentBorderStyle : 'none',
          order: calcDepth,
        }}
        className={`margin-${calcDepth}`}
        key={key}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-use-before-define -- mutual recursion with ConfigFieldByType */}
        <ConfigFieldByType definition={{
          schema: {
            ...resolvedSchema.properties[key],
            nullable: !isPropertyRequired,
          },
          key: source,
          editableBy: resolvedSchema.properties[key].editableBy ?? editableBy,
          mergeHistory: schema.mergeHistory || mergeHistory,
        }}
        fallback={fallback}
        />
      </div>
    );

  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {sections.map((section) => {

        if (!section.title) {

          return section.items.map((key) => renderPropertyField(key));

        }
        const sectionId = `${sourcePrefix}::${section.title}`;
        return (
          <CollapsibleConfigSection
            key={sectionId}
            title={section.title}
            lazyMount={lazyMountSections}
          >
            {section.items.map((key) => renderPropertyField(key))}
          </CollapsibleConfigSection>
        );

      })}
    </div>
  );

}

const StringOrEnumArrayUnionField = (props: {
  source: string;
  schema: any;
  label: string;
  required: boolean;
  sx: any;
}) => {

  const {
    source, schema, label, required, sx,
  } = props;
  const choices = schema.enum ?? schema.items?.enum ?? [];

  return (
    <FormDataConsumer>
      {({ formData }) => {

        const value = _.get(formData, source);
        if (Array.isArray(value)) {

          return (
            <SelectArrayInput
              source={source}
              choices={choices}
              label={label}
              helperText={false}
              size="small"
              fullWidth
              sx={sx}
            />
          );

        }
        return (
          <AutocompleteInput
            source={source}
            choices={choices}
            label={label}
            helperText={false}
            size="small"
            fullWidth
            sx={sx}
            isRequired={required}
          />
        );

      }}
    </FormDataConsumer>
  );

};

export function ConfigFieldByType(props: {
  definition: ClientConfigSchemaFieldDefinition;
  fallback?: ReactNode;
}) {

  const translate = useTranslate();
  const { definition, fallback } = props;
  if (!definition.schema) {

    if (fallback) {

      return <>{fallback}</>;

    }
    return (
      <div><span>
        unsupported type: unknown
      </span></div>
    );

  }

  const schema = resolveSchemaForForm(definition.schema) ?? definition.schema;

  const isRequired = () => {

    if (schema.nullable) {

      return false;

    }
    return schema['x-optional'] !== true;

  };

  const generalProps: {
    source: string;
    required: boolean;
    fullWidth: boolean;
    helperText?: string | false;
    size?: 'small' | 'medium';
    label: string;
    sx: any;
  } = {
    source: definition.key,
    required: isRequired(),
    fullWidth: true,
    helperText: false,
    size: 'small',
    label: getTitleFromKey(definition.key),
    sx: { marginBottom: '5px', marginTop: '5px' },
  };

  const refInputProps: {
    helperText: false | string;
    sx: any;
    size: 'small' | 'medium';
    fullWidth: boolean;
  } = {
    helperText: false,
    sx: { marginBottom: '5px', marginTop: '5px' },
    size: 'small',
    fullWidth: true,
  };

  if (schema[STRING_OR_ENUM_ARRAY_UNION]) {

    return (
      <StringOrEnumArrayUnionField
        source={definition.key}
        schema={schema}
        label={generalProps.label}
        required={generalProps.required}
        sx={generalProps.sx}
      />
    );

  }

  if (schema.type === 'string') {

    if (schema.format === 'date-time') {

      return (
        <DateTimeInput {...generalProps} />
      );

    }

    if (schema.enum) {

      return (
        <AutocompleteInput {...generalProps} choices={schema.enum} isRequired={generalProps.required} />
      );

    }

    const mergeHistoryValue = definition.mergeHistory?.[0]?.value;
    if (typeof mergeHistoryValue === 'string' && isColor(mergeHistoryValue)) {

      return (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5em' }}>
          <FormDataConsumer>
            {({ formData }) => {

              const value = _.get(formData, definition.key);
              if (isColor(value)) {

                return (
                  <div className="app-defaults-color-diff" style={{
                    display: 'flex',
                    flexDirection: 'row',
                  }}>
                    <div className={'app-defaults-color-diff-split'} style={{
                      backgroundColor: mergeHistoryValue,
                    }}
                    />
                    <div className={'app-defaults-color-diff-split'} style={{
                      backgroundColor: value,
                    }}/>
                  </div>
                );

              }
              return (
                <div className="app-defaults-color-diff" style={{
                  backgroundColor: mergeHistoryValue,
                }}/>
              );

            }}
          </FormDataConsumer>
          <TextInput {...generalProps} />
        </div>
      );

    }
    return (
      <TextInput
        {...generalProps}
        validate={schema.pattern
          ? regex(new RegExp(schema.pattern), translate('ra.validation.regex', { pattern: schema.pattern }))
          : undefined}
      />
    );

  }
  if (schema.type === 'number' || schema.type === 'integer') {

    const validation = [];
    if (schema.minimum !== undefined) {

      validation.push(minValue(schema.minimum));

    }
    if (schema.maximum !== undefined) {

      validation.push(maxValue(schema.maximum));

    }
    return (
      <NumberInput {...generalProps} min={schema.minimum} max={schema.maximum} validate={validation} parse={(v) => {

        if (schema.type === 'integer') {

          return parseInt(v, 10);

        }
        return parseFloat(v);

      }} />
    );

  }
  if (schema.type === 'boolean') {

    return (
      <NullableBooleanInput {...generalProps} />
    );

  }
  if (schema.type === 'object') {

    if (schema.properties?._ref) {

      return (
        <ReferenceInput {...generalProps} reference={translateResource(schema.properties._ref.enum[0])} isRequired={generalProps.required}>
          <AutocompleteInput disabled={true} label={definition.key} {...refInputProps} />
        </ReferenceInput>
      );

    }

    if (schema?.properties) {

      return (
        <SchemaObjectPropertyFields
          schema={schema}
          sourcePrefix={definition.key}
          editableBy={definition.editableBy}
          mergeHistory={schema.mergeHistory || definition.mergeHistory}
          fallback={fallback}
        />
      );

    }

  }
  if (schema.type === 'array') {

    const rawItemSchema = definition.schema.items ?? {};
    const itemSchema = resolveSchemaForForm(rawItemSchema) ?? rawItemSchema;
    const itemVariants = getOneOfVariants(rawItemSchema);
    const isDiscriminatedItems = itemVariants && isDiscriminatedObjectOneOf(itemVariants);

    if (itemSchema.type === 'object' && itemSchema.properties?._ref) {

      return (
        <div>
          <ReferenceArrayInput {...generalProps} reference={translateResource(itemSchema.properties._ref.enum[0])}>
            <AutocompleteArrayInput disabled={true} {...refInputProps} />
          </ReferenceArrayInput>
        </div>
      );

    }

    const itemEnumChoices = getSchemaEnumChoices(itemSchema);
    if (itemEnumChoices?.length) {

      return (
        <ArrayInput {...generalProps}>
          <SimpleFormIterator
            inline
            getItemLabel={(index) => `#${index + 1}`}
          >
            <AutocompleteInput
              source=""
              choices={itemEnumChoices}
              label={false}
              helperText={false}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-notchedOutline legend': {
                  display: 'none',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  top: 0,
                },
              }}
            />
          </SimpleFormIterator>
        </ArrayInput>
      );

    }

    const renderArrayItemFields = (activeItemSchema: any) => (
      <SchemaObjectPropertyFields
        schema={activeItemSchema}
        sourcePrefix=""
        editableBy={definition.editableBy}
        mergeHistory={definition.mergeHistory}
        fallback={fallback}
        lazyMountSections={false}
      />
    );

    if (isDiscriminatedItems || itemSchema.properties) {

      const firstVariant = itemVariants?.[0];
      let defaultItemSchema = itemSchema;
      if (!itemSchema.properties && firstVariant) {

        defaultItemSchema = resolveSchemaForForm(firstVariant) ?? firstVariant;

      }

      return (
        <div
          style={{
            paddingLeft: '1em',
            borderLeft: indentBorderStyle,
          }}>
          <h2 style={{ margin: '1em 0 0.5em 0', fontSize: '18px', order: 0 }}>{getTitleFromKey(definition.key)}</h2>
          <ArrayInput source={definition.key} label={false}>
            <SimpleFormIterator>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'white',
                  padding: '1em',
                  border: '1px dashed #ccc',
                }}
              >
                {isDiscriminatedItems && itemVariants ? (
                  <FormDataConsumer>
                    {({ scopedFormData }) => {

                      let variantSchema;
                      for (const discriminator of ['mergeKey', 'type']) {

                        const discValue = scopedFormData?.[discriminator];
                        variantSchema = pickOneOfVariant(itemVariants, discriminator, discValue);
                        if (variantSchema) {

                          break;

                        }

                      }
                      const activeSchema = resolveSchemaForForm(variantSchema ?? defaultItemSchema)
                        ?? variantSchema
                        ?? defaultItemSchema;
                      return renderArrayItemFields(activeSchema);

                    }}
                  </FormDataConsumer>
                ) : (
                  renderArrayItemFields(itemSchema)
                )}
              </div>
            </SimpleFormIterator>
          </ArrayInput>
        </div>
      );

    }
    if (itemSchema.type === 'number' && itemSchema.minimum !== undefined && itemSchema.maximum !== undefined) {

      const options = [];
      for (let i = itemSchema.minimum; i <= itemSchema.maximum; i++) {

        options.push({ id: i, name: i });

      }
      return (
        <SelectArrayInput {...generalProps} source={definition.key} choices={options}/>
      );

    }

    const resolvedItemForFallback = resolveSchemaForForm(rawItemSchema) ?? rawItemSchema;
    return (
      <ArrayInput source={definition.key}>
        <SimpleFormIterator>
          <ConfigFieldByType definition={{
            key: '',
            schema: resolvedItemForFallback,
            editableBy: definition.editableBy,
            mergeHistory: definition.mergeHistory,
          }}
          fallback={fallback}
          />
        </SimpleFormIterator>
      </ArrayInput>
    );

  }

  if (fallback) {

    return <>{fallback}</>;

  }
  return (
    <div><span>
      unsupported type: {schema.type} {definition.key}
    </span></div>
  );

}
